<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  ACTIVE_GAMES_HUB_ALL_EMAIL_ID,
  ACTIVE_GAMES_HUB_EMAIL_MAILBOXES,
  ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
  ACTIVE_GAMES_HUB_EMAIL_TRIAGE,
  getActiveGamesHubEmailMailbox,
  getActiveGamesHubEmailTriage,
} from 'dashboard/config/activeGamesHub';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);

const activeMailboxId = computed(
  () => getActiveGamesHubEmailMailbox(route.query.mailbox).id
);
const activeTriageId = computed(
  () => getActiveGamesHubEmailTriage(route.query.triage).id
);

const accessibleInboxIds = computed(
  () => new Set(inboxes.value.map(inbox => Number(inbox.id)))
);

const visibleMailboxes = computed(() => {
  if (!accessibleInboxIds.value.size) {
    return ACTIVE_GAMES_HUB_EMAIL_MAILBOXES;
  }

  return ACTIVE_GAMES_HUB_EMAIL_MAILBOXES.filter(mailbox => {
    if (mailbox.id === ACTIVE_GAMES_HUB_ALL_EMAIL_ID) {
      return true;
    }
    return mailbox.inboxIds.some(inboxId =>
      accessibleInboxIds.value.has(inboxId)
    );
  });
});

const mailboxLabels = computed(() => ({
  all: t('ACTIVE_GAMES_HUB.EMAIL_NAV.ALL'),
  info: t('ACTIVE_GAMES_HUB.EMAIL_NAV.INFO'),
  events: t('ACTIVE_GAMES_HUB.EMAIL_NAV.EVENTS'),
  marketing: t('ACTIVE_GAMES_HUB.EMAIL_NAV.MARKETING'),
  operations: t('ACTIVE_GAMES_HUB.EMAIL_NAV.OPERATIONS'),
  finance: t('ACTIVE_GAMES_HUB.EMAIL_NAV.FINANCE'),
}));
const triageLabels = computed(() => ({
  needs_reply: t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.NEEDS_REPLY'),
  notifications: t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.NOTIFICATIONS'),
  review: t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.REVIEW'),
  all: t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.ALL'),
}));

const unreadCountForMailbox = mailbox =>
  mailbox.inboxIds.reduce(
    (total, inboxId) => total + (getInboxUnreadCount.value(inboxId) || 0),
    0
  );

const selectMailbox = mailboxId => {
  router.replace({
    name: 'home',
    params: { accountId: route.params.accountId },
    query: {
      ...route.query,
      channel: ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
      mailbox: mailboxId,
    },
  });
};

const selectTriage = triageId => {
  router.replace({
    name: 'home',
    params: { accountId: route.params.accountId },
    query: {
      ...route.query,
      channel: ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
      triage: triageId,
    },
  });
};
</script>

<template>
  <nav
    class="border-b border-n-weak bg-n-surface-2 px-3 py-2"
    :aria-label="$t('ACTIVE_GAMES_HUB.EMAIL_NAV.LABEL')"
  >
    <div class="mb-2 grid grid-cols-2 gap-1.5">
      <button
        v-for="triage in ACTIVE_GAMES_HUB_EMAIL_TRIAGE"
        :key="triage.id"
        type="button"
        class="flex h-10 min-w-0 items-center gap-2 rounded-xl px-2.5 text-xs font-black text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          activeTriageId === triage.id
            ? triage.activeClass
            : 'bg-n-surface-1 ring-1 ring-inset ring-n-weak'
        "
        :aria-pressed="activeTriageId === triage.id"
        @click="selectTriage(triage.id)"
      >
        <Icon :icon="triage.icon" class="size-4" />
        <span class="truncate">{{ triageLabels[triage.id] }}</span>
      </button>
    </div>
    <div class="grid grid-cols-3 gap-1.5">
      <button
        v-for="mailbox in visibleMailboxes"
        :key="mailbox.id"
        type="button"
        class="relative flex h-9 min-w-0 items-center gap-1.5 rounded-xl pl-2 pr-5 text-[11px] font-bold text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          activeMailboxId === mailbox.id
            ? mailbox.activeClass
            : 'bg-n-surface-1 ring-1 ring-inset ring-n-weak'
        "
        :aria-pressed="activeMailboxId === mailbox.id"
        @click="selectMailbox(mailbox.id)"
      >
        <Icon :icon="mailbox.icon" class="size-4" />
        <span>{{ mailboxLabels[mailbox.id] }}</span>
        <span
          v-if="unreadCountForMailbox(mailbox)"
          class="absolute right-1 top-1 flex min-w-4 items-center justify-center rounded-full bg-n-ruby-9 px-0.5 text-[9px] font-black leading-4 text-white"
        >
          {{ unreadCountForMailbox(mailbox) }}
        </span>
      </button>
    </div>
  </nav>
</template>
